import { DateTime } from "luxon";
import { NeedForSweepByPeriod } from "../needForSweep/NeedForSweepByPeriod";
import { SweepingCapacityByPeriod } from "./SweepingCapacityByPeriod";
import { findSweepingCapacityByPeriod } from "./findSweepingCapacityByPeriod";
import { IllegalArgumentError } from "shared-ts-utils-core";
import { PlanByPeriods } from "./PlanByPeriods";

export class PlanByPeriod {
  /**
   * The period.
   */
  readonly timeUnit: "year" | "quarter" | "month" | "week";
  /**
   * The period.
   */
  readonly time: DateTime<true>;
  /**
   * The number of items that previously could not be planned.
   */
  readonly backlog: number;
  /**
   * The need number of items for this period.
   */
  readonly need: number;

  /**
   * backlog and need combined.
   */
  readonly backlogAndNeed: number;
  /**
   * The capacity for this period.
   */
  readonly capacity: number | null;
  /**
   * The number of items planned for this period.
   */
  readonly plan: number;

  /**
   * The unused capacity for this period.
   */
  readonly availableCapacity: number;

  /**
   * The number of remaining need this period.
   */
  readonly uncoveredNeed: number;

  constructor(args: {
    timeUnit: "year" | "quarter" | "month" | "week";
    time: DateTime<true>;
    backlog?: number;
    need: number;
    capacity: number | null;
    plan?: number;
    availableCapacity?: number;
    uncoveredNeed?: number;
  }) {
    this.timeUnit = args.timeUnit;
    this.time = args.time;
    this.capacity = args.capacity;
    this.backlog = args.backlog ?? 0;
    this.need = args.need;
    this.backlogAndNeed = this.backlog + this.need;
    this.plan = args.plan ?? 0;
    this.availableCapacity = args.availableCapacity ?? 0;
    this.uncoveredNeed = args.uncoveredNeed ?? 0;
  }

  calculatePlan(backlog = 0): PlanByPeriod {
    // The need for this period + backlog
    const actualNeed = this.need + backlog;

    const capacity = this.capacity ?? 0;
    // What we can do this period
    const plan = Math.min(capacity, actualNeed);

    //
    const newBacklog = actualNeed - plan;
    const remaining = newBacklog === 0 ? 0 : newBacklog * -1;
    const availableCapacity =
      capacity - actualNeed > 0 ? capacity - actualNeed : 0;
    return new PlanByPeriod({
      timeUnit: this.timeUnit,
      time: this.time,
      backlog: backlog,
      need: this.need,
      capacity: this.capacity,
      plan: plan,
      availableCapacity: availableCapacity,
      uncoveredNeed: remaining,
    });
  }

  increasePlan(increase: number): PlanByPeriod {
    const newPlan = this.plan + increase;
    const availableCapacity = (this.capacity ?? 0) - newPlan;
    if (availableCapacity < 0) {
      throw new IllegalArgumentError(
        "Cannot increase plan with " + increase + ". No available capacity"
      );
    }
    return new PlanByPeriod({
      timeUnit: this.timeUnit,
      time: this.time,
      backlog: this.backlog,
      need: this.need,
      capacity: this.capacity,
      plan: newPlan,
      availableCapacity: availableCapacity,
      uncoveredNeed: this.uncoveredNeed,
    });
  }

  reduceRemaining(value: number): PlanByPeriod {
    const remainingNeed = this.uncoveredNeed + value;
    return new PlanByPeriod({
      timeUnit: this.timeUnit,
      time: this.time,
      backlog: this.backlog,
      need: this.need,
      capacity: this.capacity,
      plan: this.plan,
      availableCapacity: this.availableCapacity,
      uncoveredNeed: remainingNeed,
    });
  }
}

export const toPlanByTimeUnits = (
  needForSweeps: Array<NeedForSweepByPeriod>,
  capacities: Array<SweepingCapacityByPeriod>
): PlanByPeriods => {
  if (needForSweeps.length === 0) {
    return PlanByPeriods.empty();
  }

  const expectedTimeUnit = needForSweeps[0].timeUnit;
  const capacityTimeUnit =
    capacities.length > 0 ? capacities[0].timeUnit : undefined;
  const resolvedCapacities =
    expectedTimeUnit === capacityTimeUnit
      ? capacities
      : aggregateCapacities(needForSweeps, capacities);

  return new PlanByPeriods(
    needForSweeps.map((needForSweep, index) => {
      const capacityByPeriod = resolvedCapacities.find(
        findSweepingCapacityByPeriod(needForSweep.timeUnit, needForSweep.time)
      );
      const capacity = capacityByPeriod?.capacity ?? 0;
      const need =
        index === 0
          ? Math.max(0, needForSweep.total() - needForSweep.overdue)
          : needForSweep.total();
      const uncoveredNeed = index === 0 ? needForSweep.overdue : 0;
      return new PlanByPeriod({
        timeUnit: needForSweep.timeUnit,
        time: needForSweep.time,
        need: need,
        capacity: capacity === 0 ? null : capacity,
        plan: 0,
        backlog: 0,
        uncoveredNeed: uncoveredNeed,
      });
    })
  );
};

const aggregateCapacities = (
  needForSweeps: Array<NeedForSweepByPeriod>,
  capacities: Array<SweepingCapacityByPeriod>
): Array<SweepingCapacityByPeriod> => {
  return needForSweeps.map((needForSweep) => {
    const summarizedCapacity = sumCapacity(capacities);

    return new SweepingCapacityByPeriod({
      timeUnit: summarizedCapacity.timeUnit,
      time: needForSweep.time.year,
      capacity: summarizedCapacity.sum,
    });
  });
};

const sumCapacity = (
  capacities: Array<SweepingCapacityByPeriod>
): { sum: number; timeUnit: "year" } => {
  let sum = 0;
  const aggregatedTimeUnit = capacities[0].timeUnit;
  if (aggregatedTimeUnit !== "month") {
    throw new IllegalArgumentError(
      "Can only summarize capacities with timeUnit month"
    );
  }
  capacities.forEach((it) => {
    sum += it.capacity;
  });

  return { sum, timeUnit: "year" };
};
